<template>
  <header class="kb-print-hide h-[52px] flex items-center gap-3 px-3 sm:px-5 bg-kb-bg border-b border-kb-line max-w-[100vw] overflow-hidden box-border">
    <!-- Mobile: brand (the menu lives in the tab bar) -->
    <NuxtLink
      to="/"
      class="lg:hidden flex items-center gap-2 min-w-0 overflow-hidden"
      aria-label="Home"
    >
      <img
        src="~/assets/images/logo.svg"
        alt=""
        class="h-[26px] w-auto [filter:var(--kb-logo-filter)]"
      />
      <!-- Room for the wordmark unless three actions share the bar -->
      <span
        class="kb-wordmark text-[13px] hidden"
        :class="{ 'min-[380px]:inline': !mobileBarCrowded }"
      >krewebacchus</span>
    </NuxtLink>

    <!-- Desktop: where you are, as a prompt -->
    <div
      class="hidden lg:flex items-center gap-3 min-w-0 font-mono text-[13px] text-kb-muted whitespace-nowrap"
      data-testid="prompt-path"
    >
      <span class="truncate"><b class="font-medium text-kb-accent">krewe</b> {{ promptPath }}<span class="kb-caret" /></span>
      <span
        v-if="isStaging"
        class="kb-tag kb-tag-warn"
      >staging</span>
    </div>
    <span
      v-if="isStaging"
      class="lg:hidden kb-tag kb-tag-warn"
    >staging</span>

    <button
      v-if="canSearch"
      type="button"
      class="hidden lg:flex flex-1 max-w-[440px] ml-auto items-center gap-2 h-8 pl-2.5 pr-2 rounded-[3px] border border-kb-line bg-kb-pane text-kb-dim text-[13px] text-left hover:border-kb-line-2 hover:text-kb-muted"
      data-testid="search-trigger"
      @click="openCommandPalette"
    >
      <i class="pi pi-search !text-[13px]" />
      <span class="flex-1 truncate">Search members, SubKrewes, pages</span>
      <kbd class="kb-kbd">{{ modKey }}K</kbd>
    </button>

    <div
      class="flex items-center gap-1.5 sm:gap-2"
      :class="canSearch ? 'ml-auto lg:ml-0' : 'ml-auto'"
    >
      <!-- Mobile actions -->
      <div class="flex items-center gap-1.5 lg:hidden">
        <SharedPurchaseButton
          v-if="shouldPayDues"
          ticket-type="membership"
          icon="pi pi-dollar"
          button-label="Pay"
          button-class="text-xs !px-2.5"
          button-size="small"
          no-min-width
        />
        <SharedPurchaseButton
          v-if="!shouldPayDues && canUpgradeToVip"
          ticket-type="vip"
          icon="pi pi-star"
          button-severity="help"
          button-label="VIP"
          button-class="p-button-text text-xs !px-2"
          button-size="small"
          no-min-width
        />
        <SharedPurchaseButton
          v-if="hasChildrenWithoutTickets"
          ticket-type="children"
          icon="pi pi-ticket"
          button-severity="warn"
          button-label="Kid"
          button-class="p-button-outlined text-xs !px-2"
          button-size="small"
          :children-ids="childrenWithoutTickets"
          no-min-width
          @order-complete="handleChildrenTicketsPurchased"
        />
        <SharedRedeemCodeLauncher
          v-if="shouldPayDues"
          button-size="small"
          icon-only
          class="p-button-text"
        />
        <button
          class="w-9 h-9 grid place-items-center rounded-[3px] text-kb-muted hover:text-kb-fg hover:bg-kb-hover"
          aria-label="Open chat assistant"
          @click="toggleChatbot"
        >
          <i class="pi pi-comments !text-[17px]" />
        </button>
      </div>

      <!-- Desktop actions -->
      <ul class="list-none p-0 m-0 hidden lg:flex lg:items-center gap-2 select-none">
        <li v-if="shouldPayDues">
          <SharedPurchaseButton
            ticket-type="membership"
            button-size="small"
          />
        </li>
        <li v-if="shouldPayDues">
          <SharedRedeemCodeLauncher button-size="small" />
        </li>
        <li v-if="hasChildrenWithoutTickets">
          <SharedPurchaseButton
            ticket-type="children"
            button-severity="warn"
            button-label="Buy children tickets"
            button-class="p-button-outlined"
            button-size="small"
            icon="pi pi-ticket"
            :children-ids="childrenWithoutTickets"
            @order-complete="handleChildrenTicketsPurchased"
          />
        </li>
        <li v-if="!shouldPayDues && canUpgradeToVip">
          <SharedPurchaseButton
            ticket-type="vip"
            icon="pi pi-star"
            button-severity="help"
            button-label="VIP upgrade"
            button-class="p-button-text"
            button-size="small"
          />
        </li>
        <li>
          <NotificationCenter
            class="flex px-2 py-1 items-center"
            prime-style
          />
        </li>
        <li>
          <button
            ref="userMenuButton"
            type="button"
            class="w-8 h-8 grid place-items-center rounded-full bg-kb-raised border border-kb-line-2 font-mono text-[11px] font-medium text-kb-fg hover:border-kb-muted"
            :aria-label="`Account menu for ${displayName || 'you'}`"
            @click="toggleUserMenu"
          >
            {{ initials }}
          </button>
          <Popover
            ref="userPopover"
            :show-close-icon="false"
            position="bottom"
            class="user-popover"
            @show="onUserPopoverShow"
          >
            <div class="min-w-[220px]">
              <div class="px-4 py-3 border-b border-kb-line">
                <div class="font-medium text-kb-fg">
                  {{ displayName }}
                </div>
                <div
                  v-if="roleLine"
                  class="font-mono text-[11.5px] text-kb-dim mt-0.5"
                >
                  {{ roleLine }}
                </div>
              </div>
              <NuxtLink
                to="/settings"
                class="flex items-center gap-2 px-4 py-2.5 text-kb-muted hover:text-kb-fg hover:bg-kb-hover"
                @click="userPopover?.hide()"
              >
                <i class="pi pi-cog" />
                <span>Settings</span>
              </NuxtLink>
              <a
                class="flex items-center gap-2 px-4 py-2.5 text-kb-muted hover:text-kb-fg hover:bg-kb-hover cursor-pointer"
                @click="logout"
              >
                <i class="pi pi-sign-out" />
                <span>Sign out</span>
              </a>
            </div>
          </Popover>
        </li>
      </ul>
    </div>
  </header>
</template>

<script setup lang="ts">
import { useToast } from 'primevue/usetoast'
import { computed, ref } from 'vue'
import { useCurrentUser } from 'vuefire'
import { NotificationCenter } from '~/components/notifications'
import { useChildrenTickets } from '~/composables/useChildrenTickets'
import { DuesStatusType, getMemberDuesStatus, useShouldPayDues } from '~/composables/useShouldPayDues.js'
import { MemberType, isRedshirt } from '~/shared/types/memberType'
import { memberIsAdmin, memberIsBandAdmin, memberIsRedshirtAdmin } from '~/shared/utils/memberUtils.js'

const props = defineProps({
  displayName: {
    type: String,
    default: '',
  },
  isAdmin: {
    type: Boolean,
    default: false,
  },
})

const userMenuButton = ref(null)
const userPopover = ref()
const membersStore = useMembersStore()
const currentUser = useCurrentUser()
const route = useRoute()
const { paradeYear } = useParadeYear()
const runtimeConfig = useRuntimeConfig()
const toast = useToast()

const isStaging = computed(() => runtimeConfig.public.krewebacchusEnv === 'staging')

const shouldPayDues = useShouldPayDues()

const { toggleChat: toggleChatbot } = useChatbot()

const { childrenWithoutTickets, hasChildrenWithoutTickets } = useChildrenTickets()

// Phone bar: Pay, the redeem icon and Kid together leave no room for the
// wordmark (it painted over the buttons)
const mobileBarCrowded = computed(() => shouldPayDues.value && hasChildrenWithoutTickets.value)

const member = computed(() => {
  if (!currentUser.value?.uid) return null
  return membersStore.members[`members/${currentUser.value.uid}`] ?? null
})

// The command palette is mounted for any admin-like role (see layouts/default.vue)
const canSearch = computed(() =>
  memberIsAdmin(member.value) || memberIsBandAdmin(member.value) || memberIsRedshirtAdmin(member.value),
)
const commandPaletteOpen = useState('command-palette-open', () => false)
const openCommandPalette = () => {
  commandPaletteOpen.value = true
}

const modKey = computed(() => {
  if (typeof navigator === 'undefined') return '⌘'
  return /mac/i.test(navigator.platform || navigator.userAgent) ? '⌘' : 'Ctrl '
})

const promptPath = computed(() => (route.path === '/' ? '~' : `~${route.path}`))

const initials = computed(() => {
  const name = (props.displayName || member.value?.name || '').trim()
  if (!name) return '·'
  const parts = name.split(/\s+/)
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : ''
  return (first + last).toUpperCase()
})

const roleLine = computed(() => {
  const m = member.value
  if (!m) return ''
  const roles: string[] = []
  if (memberIsAdmin(m)) roles.push('admin')
  if (memberIsBandAdmin(m)) roles.push('band admin')
  if (memberIsRedshirtAdmin(m)) roles.push('red shirt admin')
  if (m.member_type === 'captain') roles.push('captain')
  else if (m.member_type === 'redshirt') roles.push('red shirt')
  else if (m.member_type === 'member') roles.push('member')
  return roles.join(' · ')
})

const hasVip = computed(() => {
  const yearlyPurchases = member.value?.purchases?.[paradeYear.value]
  if (yearlyPurchases?.vip && Array.isArray(yearlyPurchases.vip) && yearlyPurchases.vip.length > 0) {
    return yearlyPurchases.vip.some(purchase => purchase.status === 'completed')
  }
  return false
})

const canUpgradeToVip = computed(() => {
  if (hasVip.value) return false
  const m = member.value
  if (!m) return false

  // Redshirts can upgrade to VIP (they don't pay dues, so skip dues check for them)
  if (isRedshirt(m) || m.member_type === MemberType.REDSHIRT) return true

  // Non-redshirts must have paid dues first
  if (shouldPayDues.value) return false

  // A pending or denied SubKrewe approval exempts dues but doesn't allow VIP
  const { statusType } = getMemberDuesStatus(m)
  return statusType !== DuesStatusType.EXEMPT_PENDING_APPROVAL
})

const handleChildrenTicketsPurchased = (_event: unknown) => {
  toast.add({
    severity: 'success',
    summary: 'Tickets Purchased',
    detail: `Tickets for your children have been purchased`,
    life: 3000,
  })
}

const toggleUserMenu = (event: Event) => {
  userPopover.value?.toggle(event)
}

const onUserPopoverShow = () => {
  setTimeout(() => {
    userPopover.value?.alignOverlay()
  }, 0)
}

const logout = async () => {
  userPopover.value?.hide()
  const { signOutUser } = useAuth()
  await signOutUser()
}
</script>

<style scoped>
:deep(.user-popover.p-popover) {
  max-width: 95vw;
}

:deep(.user-popover .p-popover-content) {
  padding: 0;
}
</style>
